import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { findLast } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useFragment } from "react-relay";
import { v4 as uuidv4 } from "uuid";

import { Button } from "@whoosh/design-system/src/components/controls";
import { ArrowLeftIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { PanelHeader } from "@whoosh/design-system/src/components/layout/PanelHeader";
import { VStack } from "@whoosh/design-system/src/components/primitive";

import { AddPatrons } from "../../../../../../../features/AddPatrons";
import { AddPeopleSearch_Atom } from "../../../../../../../features/AddPatrons/atoms/stores";
import { Patron } from "../../../../../../../features/AddPatrons/types";
import { getBookingPlayerNoun } from "../../../BookingSummary/utils/getBookingPlayerNoun";
import { enterEditableRoundsPrivateStepAtom } from "../../atoms/actions/EnterEditableRoundsPrivateStepAtom/enterEditableRoundsPrivateStepAtom";
import { addPrivatePlayerAtom } from "../../atoms/actions/addPlayer/addPlayerAtom";
import {
  cancelRequestGroupAtom,
  finishRequestGroupAtom,
} from "../../atoms/actions/editRequestGroups/editRequestGroups";
import { removeMemberHoldOrUnknownGuestAtom } from "../../atoms/actions/removeMemberHoldOrUnknownGuest/removeMemberHoldOrUnknownGuest";
import { removeRoundByPlayerIdAtom } from "../../atoms/actions/removeRoundByPlayerIdAtom/removeRoundByPlayerIdAtom";
import { replacePlayerOnRoundAtom } from "../../atoms/actions/replacePlayerOnRoundAtom/replacePlayerOnRoundAtom";
import {
  canReplacePlayerInGroup,
  swapOrReplacePlayerOnRoundAtom,
} from "../../atoms/actions/swapPlayersBetweenGroupsAtom/swapPlayersBetweenGroupsAtom";
import { getCurrentRoundBasedOnCurrentSessionClientIdAtom } from "../../atoms/selectors/getCurrentRoundBasedOnCurrentSessionClientIdAtom/getCurrentRoundBasedOnCurrentSessionClientIdAtom";
import {
  currentBookingAtom,
  currentModalStepAtom,
  pendingRequestGroupAtom,
} from "../../atoms/stores";
import { PatronEditBooking } from "../../types";
import { SwapPlayerCandidates } from "../SwapPlayerCandidates/SwapPlayerCandidates";
import { AddOrReplacePlayerPrivateStep_Booking$key } from "./__generated__/AddOrReplacePlayerPrivateStep_Booking.graphql";
import { AddNewPlayersHeader } from "./components/AddNewPlayersHeader/AddNewPlayersHeader";
import { GuestListHeader } from "./components/AddPatronComponents/GuestListHeader";
import { MemberHoldPlayer } from "./components/AddPatronComponents/MemberHoldPlayer";
import { MemberListHeader } from "./components/AddPatronComponents/MemberListHeader";
import { UnknownGuest } from "./components/AddPatronComponents/UnknownGuest";
import { useNewGuestSubmission } from "./hooks/useNewGuestSubmission";
import { isBookingFull as checkIsBookingFull } from "./utils/isBookingFull";
import { isGroupFullOfGuests as checkIsGroupFullOfGuests } from "./utils/isGroupFullOfGuests";

type Props = {
  bookingRef: AddOrReplacePlayerPrivateStep_Booking$key;
  membersCanAddUnknownGuests: boolean;
  memberNonPlayingHostToggle: boolean;
};
export const AddOrReplacePlayerPrivateStep = ({
  bookingRef,
  membersCanAddUnknownGuests,
  memberNonPlayingHostToggle,
}: Props) => {
  const booking = useFragment(
    AddOrReplacePlayerPrivateStepFragmentString,
    bookingRef
  );
  const currentBooking = useAtomValue(currentBookingAtom);
  const currentModalStep = useAtomValue(currentModalStepAtom);
  const pendingGroup = useAtomValue(pendingRequestGroupAtom);
  const cancelRequestGroup = useSetAtom(cancelRequestGroupAtom);
  const finishRequestGroup = useSetAtom(finishRequestGroupAtom);
  const currentModalStepType = currentModalStep.type;
  const isSwappingPlayer = currentModalStepType === "SwapPlayersPrivate";
  const isChangingPlayer =
    currentModalStepType === "ReplacePlayerPrivate" || isSwappingPlayer;
  const currentModalStepBookingIndex =
    currentModalStepType === "AddPlayerPrivate"
      ? currentModalStep.bookingIndex
      : undefined;
  const currentModalStepRoundId =
    currentModalStepType === "ReplacePlayerPrivate" ||
    currentModalStepType === "SwapPlayersPrivate"
      ? currentModalStep.roundId
      : undefined;
  const selectedBookingIndex = useMemo(() => {
    if (currentModalStepType === "AddPlayerPrivate") {
      return currentModalStep.bookingIndex ?? 0;
    }

    if (
      currentModalStepType === "ReplacePlayerPrivate" ||
      currentModalStepType === "SwapPlayersPrivate"
    ) {
      return (
        currentBooking?.rounds.find(
          (round) => round.localId === currentModalStep.roundId
        )?.bookingIndex ?? 0
      );
    }

    return null;
  }, [currentBooking?.rounds, currentModalStep, currentModalStepType]);
  const selectedBookingRounds = useMemo(
    () =>
      currentBooking?.rounds.filter(
        (round) =>
          selectedBookingIndex == null ||
          (round.bookingIndex ?? 0) === selectedBookingIndex
      ) ?? [],
    [currentBooking?.rounds, selectedBookingIndex]
  );

  const currentUserRound = useAtomValue(
    getCurrentRoundBasedOnCurrentSessionClientIdAtom
  );
  const selectedCurrentUserRound = selectedBookingRounds.find(
    (round) => round.player.id === currentUserRound?.player.id
  );

  const selectedPlayerIds = useMemo(() => {
    return (
      selectedBookingRounds.flatMap((round) =>
        round.player.id ? [round.player.id] : []
      ) ?? []
    );
  }, [selectedBookingRounds]);

  const isRequestBooking = booking?.startSlot?.__typename === "QueueSlot";
  const groupSize =
    booking.startSlot?.standardCapacity ??
    4 - (booking.startSlot?.usedCapacity ?? 0);

  const isBookingFull = checkIsBookingFull({
    nodeRequirePlayerCounts: booking.startSlot?.requirePlayerCounts,
    nodeStandardCapacity: booking.startSlot?.standardCapacity,
    isGolfCourse: booking.startSlot?.agenda?.facility?.type === "GOLF_COURSE",
    playersLength: selectedPlayerIds.length,
    groupSize: groupSize,
    bookingType: booking.type,
    capacity: booking.startSlot?.standardCapacity ?? 0,
  });

  const isGroupFullOfGuests = checkIsGroupFullOfGuests({
    nodeRequirePlayerCounts: booking.startSlot?.requirePlayerCounts,
    nodeStandardCapacity: booking.startSlot?.standardCapacity,
    isGolfCourse: booking.startSlot?.agenda?.facility?.type === "GOLF_COURSE",
    playersLength: selectedPlayerIds.length,
    groupSize: groupSize,
    bookingType: booking.type,
    capacity: booking.startSlot?.standardCapacity ?? 0,
  });

  const currentPatron: Patron = {
    playerId: selectedCurrentUserRound?.player.id || "",
    firstName: selectedCurrentUserRound?.player.firstName || "",
    lastName: selectedCurrentUserRound?.player.lastName || "",
    profileImageId: selectedCurrentUserRound?.player.profileImagePublicId || "",
    type: "Member",
    memberGroups:
      selectedCurrentUserRound?.player.clientType === "member" &&
      selectedCurrentUserRound?.player.memberGroups
        ? selectedCurrentUserRound.player.memberGroups.flatMap((g: string) =>
            g ? [g] : []
          )
        : [],
  };

  const desiredHoleCount =
    booking?.startSlot?.course?.defaultCourseLayout?.holeCount ?? "NINE";

  const courseLayoutWanted =
    booking?.startSlot?.course?.courseLayouts?.edges?.find(
      (e) => e?.node?.holeCount === desiredHoleCount
    )?.node;

  const courseLayoutId = courseLayoutWanted?.id ?? "";
  const courseLayoutCode = courseLayoutWanted?.code ?? "";

  const courseLayout = {
    id: courseLayoutId,
    code: courseLayoutCode,
    name: courseLayoutWanted?.name ?? "",
  };

  const players = selectedBookingRounds.map((round) => round.player);

  const [search, setSearch] = useAtom(AddPeopleSearch_Atom);
  const replacePlayerOnRound = useSetAtom(replacePlayerOnRoundAtom);
  const swapOrReplacePlayerOnRound = useSetAtom(swapOrReplacePlayerOnRoundAtom);
  const addPlayerToBooking = useSetAtom(addPrivatePlayerAtom);
  const removeRoundByPlayerId = useSetAtom(removeRoundByPlayerIdAtom);
  const removeMemberHoldOrUnknownGuest = useSetAtom(
    removeMemberHoldOrUnknownGuestAtom
  );

  const onNewGuestSubmission = useNewGuestSubmission({
    courseLayout,
    enterEditableRoundsPrivateStep: () => {
      if (!pendingGroup) enterEditableRoundsPrivateStep();
    },
  });

  const enterEditableRoundsPrivateStep = useSetAtom(
    enterEditableRoundsPrivateStepAtom
  );

  const replacingRound = isChangingPlayer
    ? currentBooking?.rounds.find(
        (round) => round.localId === currentModalStepRoundId
      )
    : null;

  const isReplacingRoundGuest =
    replacingRound?.player.clientType === "guest" ||
    replacingRound?.player.clientType === "unknownGuest";

  const replacingPlayer = replacingRound?.player;
  const playerNoun = getBookingPlayerNoun({
    bookingType: currentBooking?.type,
    copyOverrides: currentBooking?.copyOverrides,
  });
  const replacingPlayerName =
    (replacingPlayer?.clientType === "guest" ||
      replacingPlayer?.clientType === "unknownGuest" ||
      replacingPlayer?.clientType === "member") &&
    replacingPlayer?.firstName &&
    replacingPlayer?.lastName
      ? `${replacingPlayer.firstName} ${replacingPlayer.lastName}`
      : replacingPlayer?.clientType === "memberHold"
      ? t`Member Hold`
      : t`New ${playerNoun.singular}`;

  const panelHeadertitle = pendingGroup
    ? t`Add Group`
    : isSwappingPlayer
    ? t`Swap with another player`
    : isChangingPlayer
    ? t`Replace ${replacingPlayerName}`
    : t`Add ${playerNoun.singular}`;

  const [isAddingNewGuest, setIsAddingNewGuest] = useState(false);

  const onCheckPlayer = useCallback(
    (player: Patron, checked: boolean) => {
      if (isChangingPlayer && !checked) return;
      if (checked) {
        // Map the BookingPlayer to the appropriate RoundPlayer type based on player type
        let roundPlayer: PatronEditBooking.RoundPlayer;
        if (player.type === "Member") {
          roundPlayer = {
            id: player.playerId || player.internalIdentifier || uuidv4(),
            clientType: "member" as const,
            firstName: player.firstName || null,
            lastName: player.lastName || null,
            profileImagePublicId: player.profileImageId || null,
            membershipTypeId: player.membershipTypeId ?? undefined,
            memberGroups: player.memberGroups || [],
          };
        } else if (player.type === "Guest") {
          roundPlayer = {
            id: player.playerId || player.internalIdentifier || uuidv4(),
            clientType: "guest" as const,
            firstName: player.firstName || null,
            lastName: player.lastName || null,
            profileImagePublicId: player.profileImageId || null,
          };
        } else if (player.type === "Unknown Guest") {
          roundPlayer = {
            id: null,
            clientType: "unknownGuest" as const,
            firstName: null,
            lastName: null,
          };
        } else if (player.type === "Member Hold") {
          roundPlayer = {
            id: null,
            clientType: "memberHold" as const,
            firstName: null,
            lastName: null,
          };
        } else {
          // Default to guest if type is not recognized
          roundPlayer = {
            id: player.playerId || player.internalIdentifier || uuidv4(),
            clientType: "guest" as const,
            firstName: player.firstName || null,
            lastName: player.lastName || null,
            profileImagePublicId: player.profileImageId || null,
          };
        }

        if (isSwappingPlayer) {
          const changed = swapOrReplacePlayerOnRound({
            player: roundPlayer,
            roundId: currentModalStepRoundId ?? "",
          });
          if (!changed) return;
        } else if (currentModalStepType === "ReplacePlayerPrivate") {
          replacePlayerOnRound({
            player: roundPlayer,
            roundId: currentModalStepRoundId ?? "",
          });
        } else if (
          currentBooking?.type === "Golf" ||
          currentBooking?.type === "Duration" ||
          currentBooking?.type === "Lesson"
        ) {
          addPlayerToBooking({
            player: roundPlayer,
            bookingIndex: currentModalStepBookingIndex,
          });
        }
      } else {
        removeRoundByPlayerId(
          player.playerId || player.internalIdentifier || ""
        );
      }

      if (isChangingPlayer) {
        enterEditableRoundsPrivateStep();
      }
    },
    [
      addPlayerToBooking,
      enterEditableRoundsPrivateStep,
      currentModalStepBookingIndex,
      currentModalStepRoundId,
      currentModalStepType,
      isChangingPlayer,
      isSwappingPlayer,
      swapOrReplacePlayerOnRound,
      replacePlayerOnRound,
      removeRoundByPlayerId,
      currentBooking?.type,
    ]
  );

  const onAddUnknownGuest = useCallback(() => {
    const unknownGuestPlayer: PatronEditBooking.RoundPlayer = {
      id: null,
      clientType: "unknownGuest" as const,
      firstName: null,
      lastName: null,
    };

    if (isSwappingPlayer) {
      if (
        swapOrReplacePlayerOnRound({
          player: unknownGuestPlayer,
          roundId: currentModalStepRoundId ?? "",
        })
      )
        enterEditableRoundsPrivateStep();
    } else if (
      currentBooking?.type === "Golf" ||
      currentBooking?.type === "Duration" ||
      currentBooking?.type === "Lesson"
    ) {
      addPlayerToBooking({
        player: unknownGuestPlayer,
        bookingIndex: currentModalStepBookingIndex,
      });
    }
  }, [
    addPlayerToBooking,
    currentBooking?.type,
    currentModalStepBookingIndex,
    isSwappingPlayer,
    swapOrReplacePlayerOnRound,
    currentModalStepRoundId,
    enterEditableRoundsPrivateStep,
  ]);

  const onRemoveUnknownGuest = useCallback(() => {
    const guestToRemove = findLast(
      players,
      (p) => p.clientType === "unknownGuest"
    );

    if (guestToRemove) {
      removeMemberHoldOrUnknownGuest("unknownGuest");
    }
  }, [players, removeMemberHoldOrUnknownGuest]);

  const isGolfCourse =
    booking.startSlot?.agenda?.facility?.type === "GOLF_COURSE";

  const excludedOrSelectedRounds = isSwappingPlayer
    ? currentBooking?.rounds ?? []
    : selectedBookingRounds;
  const allPlayers =
    excludedOrSelectedRounds.map(
      (round: PatronEditBooking.Round) => round.player.id ?? ""
    ) ?? [];

  const memberRounds = selectedBookingRounds.filter(
    (round: PatronEditBooking.Round) =>
      ["member", "memberHold"].includes(round.player.clientType)
  );

  const showGuestListOverlay = !isSwappingPlayer && memberRounds?.length === 0;

  const currentPlayers =
    excludedOrSelectedRounds.flatMap((round: PatronEditBooking.Round) =>
      round.player.id ? [round.player.id] : []
    ) ?? [];

  const unknownGuestPlayers = selectedBookingRounds.filter(
    (round: PatronEditBooking.Round) =>
      round.player.clientType === "unknownGuest"
  );

  const memberHoldPlayers = selectedBookingRounds.filter(
    (round: PatronEditBooking.Round) => round.player.clientType === "memberHold"
  );

  const isLesson = booking?.__typename === "LessonBooking";

  const controlledCapacity = !booking?.startSlot?.requirePlayerCounts
    ? isRequestBooking
      ? booking.startSlot?.standardCapacity || 0
      : (booking.startSlot?.standardCapacity || 0) -
        (booking.startSlot?.usedCapacity || 0)
    : booking.startSlot?.standardCapacity || 0;

  const capacityOnBooking =
    controlledCapacity !== 0 ? controlledCapacity ?? 4 : 4;
  const isAggregateRequest =
    currentBooking?.rounds.some((round) => round.bookingIndex != null) ?? false;
  const aggregateGroupCapacity =
    currentBooking?.groupCapacity ?? capacityOnBooking;
  const hasAvailableAggregateGroupCapacity =
    !isAggregateRequest ||
    selectedBookingRounds.filter((round) => !round.isNonPlayingHost).length <
      aggregateGroupCapacity;

  const areGuestsAllowed = isSwappingPlayer
    ? canReplacePlayerInGroup(
        currentBooking?.rounds ?? [],
        currentModalStepRoundId ?? "",
        {
          id: null,
          clientType: "unknownGuest",
          firstName: null,
          lastName: null,
        }
      )
    : !isChangingPlayer || isReplacingRoundGuest;
  const areMembersAllowed =
    isSwappingPlayer || !isChangingPlayer || !isReplacingRoundGuest;
  const canReplaceWithMemberHold =
    !isSwappingPlayer ||
    canReplacePlayerInGroup(
      currentBooking?.rounds ?? [],
      currentModalStepRoundId ?? "",
      { id: null, clientType: "memberHold", firstName: null, lastName: null }
    );

  const isCapacityReachedForGuests =
    (isAggregateRequest
      ? !hasAvailableAggregateGroupCapacity
      : memberNonPlayingHostToggle
      ? isGroupFullOfGuests
      : isBookingFull) && !isChangingPlayer;
  const isCapacityReachedForMembers =
    (isAggregateRequest
      ? !hasAvailableAggregateGroupCapacity
      : isBookingFull) && !isChangingPlayer;

  return (
    <VStack flex={1} testID="add-or-replace-player-private-step-container">
      <PanelHeader
        paddingVariant="medium"
        header={panelHeadertitle}
        outerLeftComponent={
          <Button
            accessibilityLabel={t`Close`}
            testID="modal-close-btn"
            variant="secondary"
            onPress={() => {
              if (pendingGroup) cancelRequestGroup();
              else enterEditableRoundsPrivateStep();
            }}
            leftIcon={<ArrowLeftIcon />}
          />
        }
      />

      {/*
        AddNewPlayersHeader is pinned above AddPatrons; AddPatrons owns the
        single scroll surface (its FlashList). A wrapping ScrollView here would
        nest around that FlashList and reintroduce the ENG-2362 scroll bug.
      */}
      {currentModalStep.type === "AddPlayerPrivate" ? (
        <AddNewPlayersHeader
          nodeRef={booking.startSlot ?? null}
          isLesson={isLesson}
          isGolfCourse={isGolfCourse}
          isRequestBooking={isRequestBooking}
          selectedPlayers={players}
        />
      ) : null}
      <AddPatrons
        disabledPlayerIds={
          isSwappingPlayer
            ? []
            : currentBooking?.rounds.flatMap((round) =>
                !selectedBookingRounds.includes(round) && round.player.id
                  ? [round.player.id]
                  : []
              ) ?? []
        }
        includeTransportationType={isGolfCourse}
        currentPatron={currentPatron.playerId ? currentPatron : undefined}
        search={search}
        setSearch={setSearch}
        handleAddMemberClick={onCheckPlayer}
        handleAddGuestClick={onCheckPlayer}
        showGuestListOverlay={showGuestListOverlay}
        isGroupFullOfAttendees={false}
        isGroupFullOfGuests={isCapacityReachedForGuests}
        isGroupFullOfMembers={isCapacityReachedForMembers}
        checkNewDisabled={isCapacityReachedForGuests}
        keepItemInListAfterCheck={!isChangingPlayer}
        selectionAction={isSwappingPlayer ? "replace" : undefined}
        unknownGuestsEnabled={membersCanAddUnknownGuests}
        canAddNewGuest={areGuestsAllowed}
        isAddingNewGuest={isAddingNewGuest}
        setIsAddingNewGuest={setIsAddingNewGuest}
        onNewGuestSubmission={onNewGuestSubmission}
        onAddUnknownGuest={onAddUnknownGuest}
        onRemoveUnknownGuest={onRemoveUnknownGuest}
        areGuestsAllowed={areGuestsAllowed}
        areMembersAllowed={areMembersAllowed}
        bg="surface-primary"
        players={currentPlayers}
        allPlayers={allPlayers}
        unknownGuestCount={unknownGuestPlayers?.length ?? 0}
        memberHoldCount={memberHoldPlayers?.length ?? 0}
        memberListHeader={
          isSwappingPlayer ? (
            <>
              <SwapPlayerCandidates
                roundId={currentModalStepRoundId ?? ""}
                playerType="member"
                search={search}
              />
              {canReplaceWithMemberHold ? (
                <MemberHoldPlayer replacingRound={replacingRound} />
              ) : null}
            </>
          ) : currentModalStep.type === "AddPlayerPrivate" ? (
            <MemberListHeader capacityOnBooking={capacityOnBooking} />
          ) : currentModalStep.type === "ReplacePlayerPrivate" &&
            areMembersAllowed ? (
            <MemberHoldPlayer replacingRound={replacingRound} />
          ) : null
        }
        guestListHeader={
          isSwappingPlayer ? (
            <>
              <SwapPlayerCandidates
                roundId={currentModalStepRoundId ?? ""}
                playerType="guest"
                search={search}
              />
              {areGuestsAllowed ? (
                <UnknownGuest
                  replacingRound={replacingRound}
                  membersCanAddUnknownGuests={membersCanAddUnknownGuests}
                />
              ) : null}
            </>
          ) : currentModalStep.type === "AddPlayerPrivate" ? (
            <GuestListHeader
              onAddUnknownGuest={onAddUnknownGuest}
              onRemoveUnknownGuest={onRemoveUnknownGuest}
              capacityOnBooking={capacityOnBooking}
              membersCanAddUnknownGuests={membersCanAddUnknownGuests}
            />
          ) : currentModalStep.type === "ReplacePlayerPrivate" &&
            areGuestsAllowed ? (
            <UnknownGuest
              replacingRound={replacingRound}
              membersCanAddUnknownGuests={membersCanAddUnknownGuests}
            />
          ) : null
        }
      />
      {currentModalStep.type === "AddPlayerPrivate" ? (
        <VStack
          padding={5}
          borderTopWidth={1.5}
          borderTopColor="outline-default"
        >
          <Button
            testID="add-players-private-continue-button"
            accessibilityLabel={t`Continue`}
            isDisabled={
              !!pendingGroup &&
              !selectedBookingRounds.some((round) => !!round.player.id)
            }
            onPress={() => {
              if (pendingGroup) finishRequestGroup();
              else enterEditableRoundsPrivateStep();
            }}
          >
            {t`Continue`}
          </Button>
        </VStack>
      ) : null}
    </VStack>
  );
};

const AddOrReplacePlayerPrivateStepFragmentString = graphql`
  fragment AddOrReplacePlayerPrivateStep_Booking on Booking {
    __typename
    type
    startSlot {
      ... on TimeSlot {
        ...AddNewPlayersHeaderNode
        standardCapacity: capacity(bookingType: STANDARD)
        usedCapacity
        requirePlayerCounts
        course {
          id
          name
          defaultCourseLayout {
            id
            holeCount
          }
          courseLayouts {
            edges {
              node {
                id
                code
                holeCount
                name
              }
            }
          }
        }
      }
      ... on QueueSlot {
        __typename
        ...AddNewPlayersHeaderNode
      }
      club {
        id
      }
      agenda {
        facility {
          type
        }
      }
    }
  }
`;
